import React from "react";
import { motion } from "framer-motion";

const CHAPTERS = [
    {
        n: "01",
        title: "Insight isn't enough.",
        body: "You can know why you feel the way you do and still feel it in the body. Healing has to reach lower than language — down into the nervous system, the breath, the parts that never learned safety.",
    },
    {
        n: "02",
        title: "The body isn't a problem.",
        body: "It's a place you're returning to. Every tension, every hesitation, every held breath is holding a story. We meet the body with reverence, not repair.",
    },
    {
        n: "03",
        title: "Slowness is the medicine.",
        body: "Nothing here rushes you. Not the session, not the modality, not the outcome. Trust is built at the pace of the softest part of you — because that's the part we're here for.",
    },
    {
        n: "04",
        title: "Modern psychology meets soul.",
        body: "Clinical training and spiritual literacy, held in one room. Neither is bypassed. Neither is preached. Both are used, quietly, only when they're needed.",
    },
];

const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};
const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    },
};

export default function ManifestoSection() {
    return (
        <section
            className="bg-[#2C2B29] text-[#EFEBE4] relative overflow-hidden"
            data-testid="manifesto-section"
        >
            {/* faint background glow */}
            <div
                className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(197,168,128,0.14), transparent)",
                }}
                aria-hidden="true"
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, margin: "-15% 0px" }}
                    className="max-w-3xl"
                >
                    <p className="ssh-eyebrow text-[#C5A880]">
                        Manifesto · What this practice believes
                    </p>
                    <h2 className="mt-6 font-serif font-light tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl text-white">
                        Four quiet beliefs that shape{" "}
                        <em className="italic text-[#C5A880]">every session.</em>
                    </h2>
                </motion.div>

                <motion.ol
                    variants={container}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-10% 0px" }}
                    className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-14"
                >
                    {CHAPTERS.map((c) => (
                        <motion.li
                            key={c.n}
                            variants={itemVariants}
                            className="group relative pl-16 border-l border-[#4A4947] hover:border-[#C5A880] transition-colors"
                            data-testid={`manifesto-${c.n}`}
                        >
                            <span
                                className="absolute -left-[1px] -top-2 font-serif italic text-6xl text-[#C5A880] leading-none"
                                aria-hidden="true"
                            >
                                {c.n}
                            </span>
                            <h3 className="font-serif text-3xl lg:text-4xl leading-tight text-white">
                                {c.title}
                            </h3>
                            <p className="mt-5 text-base lg:text-lg leading-[1.75] text-[#d8d3c8] max-w-lg">
                                {c.body}
                            </p>
                        </motion.li>
                    ))}
                </motion.ol>
            </div>
        </section>
    );
}
